import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  forwardRef,
  inject,
  input,
  signal,
  TemplateRef,
  viewChild,
  ViewChild,
  viewChildren,
  ViewContainerRef,
} from '@angular/core';
import { MatAccordion, MatExpansionPanel, MatExpansionPanelHeader } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoPipe } from '@jsverse/transloco';
import { AttachmentDialogComponent, AttachmentWithImported } from 'guideline/feature-attachment';
import { produce } from 'immer';
import { ExpandService } from 'libs/shared/util-common/src/lib/expand.service';
import { has } from 'lodash-es';
import { GuidelineService, GuidelineViewService, ScrollService, SelectionService } from 'shared/domain';
import { ButtonDirective, ChipComponent, DialogService, IconDirective, PopoverService, TooltipDirective } from 'shared/ui-common';
import { isConnectedGuideline, MobileTextScrollDirective } from 'shared/util-common';

import { CommentableElementDirective } from '@treetop/shared/comments';
import { AttachmentsLinksReferencesTag } from '@treetop/shared/guideline-tags';
import {
  ChapterWidget,
  GuidelineChapterType,
  GuidelineOrigin,
  GuidelineTag,
  GuidelineTagStatus,
  GuidelineWidget,
  ResourceTag,
  UpdateStatus,
} from '@treetop/shared/model';
import { AddResourceTagsComponent, ResourceTagListComponent } from '@treetop/shared/tt-resource-tags';

import { ChapterWidgetListComponent } from './chapter-list/chapter-widget-list.component';
import { GuidelineUpdateTagComponent } from '../../../../../guideline-edit/edit-widgets/edit-chapter-widget/guideline-update-tag/guideline-update-tag.component';
import { WidgetComponent } from '../../widget.component';

@Component({
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'tt-chapter-widget',
  templateUrl: './chapter-widget.component.html',
  styleUrls: ['./chapter-widget.component.scss'],
  imports: [
    WidgetComponent,
    MatExpansionPanel,
    TranslocoPipe,
    ChipComponent,
    AttachmentsLinksReferencesTag,
    IconDirective,
    MatIconModule,
    TooltipDirective,
    GuidelineUpdateTagComponent,
    forwardRef(() => ChapterWidgetListComponent),
    MatExpansionPanelHeader,
    MobileTextScrollDirective,
    MatAccordion,
    CommentableElementDirective,
    ButtonDirective,
    AddResourceTagsComponent,
    ResourceTagListComponent,
  ],
  host: {
    '[attr.data-chapter-title]': 'chapter()?.title',
    '[attr.data-widget-id]': 'chapterId()',
    '[attr.data-uniqueid]': 'chapterId()',
    '[attr.data-widget-type]': '"chapter"',
  },
})
export class ChapterWidgetComponent implements AfterViewInit {
  protected guidelineViewService = inject(GuidelineViewService, { optional: true });
  protected expandService = inject(ExpandService);
  private guidelineService = inject(GuidelineService);
  private selectionService = inject(SelectionService);
  private scrollService = inject(ScrollService);
  private elementRef = inject(ElementRef<HTMLElement>);
  private dialogService = inject(DialogService);
  private popoverService = inject(PopoverService);
  private viewContainerRef = inject(ViewContainerRef);

  private tagTriggerButton = viewChild<ElementRef>('tagTriggerButton');
  private addResourceTagsTemplate = viewChild<TemplateRef<unknown>>('addResourceTagsTemplate');

  public chapterId = input.required<string>();
  public chapter = computed(() => this.guidelineService.selectElement<ChapterWidget>(this.chapterId())());
  protected numbering = computed<string>(() => this.guidelineService.numberedChapters()[this.chapter()?.uniqueId] ?? '');

  @ViewChild('accordion') public accordion?: MatAccordion;
  @ViewChild('panel') public panel?: MatExpansionPanel;

  protected widgetComponents = viewChildren(WidgetComponent);
  protected guidelineData = this.guidelineService.guidelineData;
  protected allowEditTags = this.guidelineViewService?.allowEditTags ?? signal(false);
  protected chapterLevel = computed(() => {
    const levels = this.numbering()?.split('.') ?? [];
    const numberOfLevels = levels.length;
    return 'level-' + numberOfLevels;
  });

  protected isCompareMode = signal(false);
  protected isEditMode = signal(false);

  protected expanded = computed(() => this.expandService.getExpandState(this.chapter().uniqueId)());

  protected showEditImportedGuidelineButtons = computed(() => {
    if (this.chapter().tag?.status === GuidelineTagStatus.DELETED) {
      return false;
    }

    return this.guidelineViewService?.isEditImportedGuidelineMode() || this.guidelineViewService?.isMergeMode();
  });

  protected selectedResourceTags = computed(() => {
    const importedResourceTags = this.chapter()?.resourceTags ?? [];
    const localResourceTags = this.chapter()?.localChanges?.resourceTags ?? [];
    if (!localResourceTags?.length) {
      return importedResourceTags;
    } else {
      return localResourceTags;
    }
  });

  protected attachments = computed(() => {
    const chapter = this.chapter();
    if (!chapter) {
      return [];
    }

    const chapterAttachments = (chapter.attachments ?? []).filter(
      (attachment) => chapter.localChanges?.deletedOriginalAttachmentIds?.includes(attachment.id) !== true,
    );

    const localAttachments = this.chapter()?.localChanges?.attachments ?? [];

    return [...chapterAttachments, ...localAttachments];
  });

  protected originalTag = computed(() => {
    if (!this.chapter()) {
      return undefined;
    }

    const originalTag = this.chapter().tag;

    if (!originalTag) {
      return undefined;
    }

    if (isConnectedGuideline(this.guidelineData().guidelineOrigin)) {
      if (this.guidelineData().updateStatus === UpdateStatus.DEFAULT) {
        return undefined;
      }

      if (
        !this.guidelineViewService?.isSmartbookMode() &&
        !this.guidelineViewService?.isPreviewMode() &&
        !this.guidelineData().environment.hasPublishedVersion
      ) {
        return undefined;
      }
    }

    return originalTag;
  });

  protected localTag = computed(() => {
    const originalTag = this.originalTag();
    const localTag = this.chapter().localChanges?.tag;

    if (!localTag) {
      return undefined;
    }

    // If the original tag is deleted, we don't show local tag
    if (originalTag?.status === GuidelineTagStatus.DELETED) {
      return undefined;
    }

    if (!this.guidelineViewService?.isSmartbookMode() && !this.guidelineData().environment.hasPublishedVersion) {
      return undefined;
    }

    return localTag;
  });

  protected tag = computed(() => {
    if (!this.guidelineViewService?.showTags()) {
      return undefined;
    }

    if (this.childrenMergeConflictStatuses().length > 0) {
      return undefined;
    }

    return this.localTag() ?? this.originalTag();
  });

  protected hasContent = computed(() => {
    const widgets =
      this.chapter()
        ?.widgetIds?.map((widgetId) => this.guidelineService.selectElement(widgetId)())
        .filter(Boolean) ?? [];

    if (!widgets || !widgets.length) {
      return false;
    }

    for (const widget of widgets) {
      if (widget?.widgetType === 'text') {
        if (widget.localChanges?.text === '' ? undefined : (widget.localChanges?.text ?? widget.text)) {
          return true;
        }
      }

      if (widget?.widgetType === 'component-list') {
        if (widget.widgetIds?.length > 0) {
          return true;
        }
      }

      if (widget?.widgetType === 'flowchart') {
        if (widget.elements?.length > 0) {
          return true;
        }
      }

      if (widget?.widgetType === 'workflow') {
        if (widget.workflowId && widget.workflowVersion) {
          return true;
        }
      }
    }

    return false;
  });

  protected widgets = computed<GuidelineWidget[]>(() => {
    if (!this.chapter() || !this.chapter()?.widgetIds) {
      return [];
    }

    const selectedWidgets = this.chapter().widgetIds.map((widgetId) => this.guidelineService.selectElement(widgetId)());

    return selectedWidgets.filter((widget): widget is GuidelineWidget => !!widget);
  });

  private childrenMergeConflictStatuses = computed(() => {
    return this.widgetComponents()
      .map((widget) => widget.mergeConflictStatus())
      .filter((status) => !!status);
  });

  protected handleResourceTagLocalChanges(updatedTags: ResourceTag[]) {
    const updatedLocalChanges = produce(this.chapter()?.localChanges ?? {}, (draft) => {
      draft.resourceTags = updatedTags;
    });

    this.guidelineService.updateWidgetLocal(this.chapter()?.uniqueId, updatedLocalChanges);
  }

  protected openTagDropdown(event: MouseEvent): void {
    event.stopPropagation();

    const triggerEl = this.tagTriggerButton();
    const template = this.addResourceTagsTemplate();
    if (!triggerEl || !template) return;

    this.popoverService.open(template, triggerEl.nativeElement, { panelClass: 'tt-tag-dropdown-panel' }, this.viewContainerRef);
  }

  protected removeTag(tag: ResourceTag): void {
    this.handleResourceTagLocalChanges(this.selectedResourceTags().filter((t) => t.id !== tag.id));
  }

  protected chapterHasLocalChanges = computed(() => {
    if (!this.chapter() || !this.chapter().localChanges) {
      return false;
    }

    return this.chapter().localChanges.attachments?.length > 0 || this.chapter()?.localChanges.deletedOriginalAttachmentIds?.length > 0;
  });

  protected childrenHaveLocalChanges = computed(() => {
    return this.widgetComponents().some((widget) => widget.hasLocalChanges());
  });

  protected hasMergeConflict = computed(() => {
    return this.childrenMergeConflictStatuses().some((conflict) => conflict === 'conflict');
  });

  protected selection = '';

  constructor() {
    effect(() => {
      this.expandService.addItem(this.chapter().uniqueId, true, this.chapter()?.parentId);
    });
  }

  public ngAfterViewInit(): void {
    this.scrollService.register(this.elementRef);
  }

  protected toggleEditMode($event: MouseEvent): void {
    $event.stopPropagation();

    this.isEditMode.set(!this.isEditMode());
    this.selectionService.clearSelection();

    if (this.isEditMode()) {
      this.panel?.open();
    }
  }

  protected toggleCompareMode($event: MouseEvent): void {
    $event.stopPropagation();

    if (this.hasMergeConflict()) {
      return;
    }

    this.isCompareMode.set(!this.isCompareMode());

    if (this.isCompareMode()) {
      this.panel?.open();
    }
  }

  protected openAttachmentDialog($event: MouseEvent): void {
    $event.stopPropagation();

    const localAttachments: AttachmentWithImported[] = (this.chapter().localChanges?.attachments || []).map((attachment) => ({
      ...attachment,
    }));

    const importedAttachments: AttachmentWithImported[] = (this.chapter().attachments || []).map((attachment) => ({
      ...attachment,
      imported: true,
    }));

    const allAttachments = [...localAttachments, ...importedAttachments];
    const uniqueAttachments = Array.from(new Map(allAttachments.map((attachment) => [attachment.id, attachment])).values());

    const dialogRef = this.dialogService.open(AttachmentDialogComponent, {
      title: 'Assets',
      maxWidth: 1200,
      panelClass: 'attachment-dialog',
      disableClose: true,
      data: {
        attachedResources: uniqueAttachments ? uniqueAttachments : [],
        deletedImportedAttachmentIds: this.chapter().localChanges?.deletedOriginalAttachmentIds ?? [],
      },
    });

    dialogRef.afterClosed().subscribe((result: { attachedResources: AttachmentWithImported[]; deletedImportedResourceIds: string[] }) => {
      if (result) {
        this.assignAttachments(result.attachedResources, result.deletedImportedResourceIds);
      }
    });
  }

  private assignAttachments(attachments: AttachmentWithImported[], deletedImportedAttachmentsIds: string[] = []): void {
    const updatedLocalChanges = produce(this.chapter()?.localChanges ?? {}, (draft) => {
      draft.attachments = attachments.filter((attachment) => !attachment.imported);
      draft.deletedOriginalAttachmentIds = [...(draft?.deletedOriginalAttachmentIds ?? []), ...deletedImportedAttachmentsIds];
    });

    this.guidelineService.updateWidgetLocal(this.chapter()?.uniqueId, updatedLocalChanges);
  }

  protected handleTagUpdated(tag: GuidelineTag) {
    this.guidelineService.updateWidgetTag(this.chapter()?.uniqueId, tag);
  }

  protected handleExpandedChange(expanded: boolean) {
    this.expandService.setExpandState(this.chapter().uniqueId, expanded);
  }

  protected GuidelineChapterType = GuidelineChapterType;
  protected GuidelineOrigin = GuidelineOrigin;
  protected GuidelineTagStatus = GuidelineTagStatus;
  protected readonly has = has;
}
